/**
 * Advanced Content Detection and Classification System
 * Analyzes text content to determine optimal rendering strategy
 */

export const ContentTypes = {
  TABLE: 'table',
  JSON: 'json',
  CODE: 'code',
  LIST: 'list',
  STRUCTURED_DATA: 'structured_data',
  MARKDOWN: 'markdown',
  CHART_DATA: 'chart_data',
  ERROR: 'error',
  WARNING: 'warning',
  SUCCESS: 'success',
  MIXED: 'mixed'
};

/**
 * Detects the primary content type of a text block
 * @param {string} content - The content to analyze
 * @returns {Object} Detection result with type and confidence
 */
export const detectContentType = (content) => {
  if (!content || typeof content !== 'string') {
    return { type: ContentTypes.MARKDOWN, confidence: 1.0, metadata: {} };
  }

  const detectors = [
    detectTable,
    detectJSON,
    detectCode,
    detectStructuredData,
    detectList,
    detectChartData,
    detectAlertType
  ];

  const results = detectors.map(detector => detector(content));
  const highestConfidence = results.reduce((max, current) => 
    current.confidence > max.confidence ? current : max,
    { type: ContentTypes.MARKDOWN, confidence: 0.3, metadata: {} }
  );

  // Check for mixed content
  const significantTypes = results.filter(r => r.confidence > 0.5);
  if (significantTypes.length > 1) {
    return {
      type: ContentTypes.MIXED,
      confidence: 0.9,
      metadata: {
        types: significantTypes.map(t => t.type),
        primary: highestConfidence.type
      }
    };
  }

  return highestConfidence;
};

/**
 * Detects table-like structures in content
 */
const detectTable = (content) => {
  const indicators = {
    pipeSeparated: /\|.*\|.*\|/m,
    headerDivider: /[-|+]{3,}/m,
    alignedColumns: /^(\S+\s{2,}){2,}/m,
    tabSeparated: /\t.*\t/m,
    colonSeparated: /^\s*\w+\s*:\s*\S+/m
  };

  let score = 0;
  let tableType = null;

  if (indicators.pipeSeparated.test(content)) {
    score += 0.9;
    tableType = 'pipe';
  }
  if (indicators.headerDivider.test(content)) {
    score += 0.3;
  }
  if (indicators.alignedColumns.test(content)) {
    score += 0.5;
    tableType = tableType || 'aligned';
  }
  if (indicators.tabSeparated.test(content)) {
    score += 0.7;
    tableType = tableType || 'tab';
  }

  // Check for consistent row structure
  const lines = content.split('\n').filter(l => l.trim());

  // Filter out border lines (for box tables) before checking consistency
  const dataLines = lines.filter(line => {
    // Skip border lines like +---+---+ or +=====+
    return !/^\+[-=+\s]+\+$/.test(line.trim());
  });

  const columnCounts = dataLines.map(line => {
    if (tableType === 'pipe') return (line.match(/\|/g) || []).length;
    if (tableType === 'tab') return (line.match(/\t/g) || []).length + 1;
    return (line.match(/\s{2,}/g) || []).length + 1;
  });

  // Only calculate consistency if we have data lines
  let consistency = 1.0;
  let avgColumns = 0;

  if (columnCounts.length > 0) {
    avgColumns = columnCounts.reduce((a, b) => a + b, 0) / columnCounts.length;
    consistency = columnCounts.filter(c => Math.abs(c - avgColumns) <= 1).length / columnCounts.length;
  }

  score *= consistency;

  return {
    type: ContentTypes.TABLE,
    confidence: Math.min(score, 1.0),
    metadata: { tableType, avgColumns, consistency }
  };
};

/**
 * Detects JSON structures
 */
const detectJSON = (content) => {
  const trimmed = content.trim();
  
  // Check for JSON-like structure
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try {
      JSON.parse(trimmed);
      return { type: ContentTypes.JSON, confidence: 1.0, metadata: { valid: true } };
    } catch {
      return { type: ContentTypes.JSON, confidence: 0.4, metadata: { valid: false } };
    }
  }

  // Check for JSON code blocks
  if (/```json[\s\S]*```/i.test(content)) {
    return { type: ContentTypes.JSON, confidence: 0.9, metadata: { inCodeBlock: true } };
  }

  return { type: ContentTypes.JSON, confidence: 0, metadata: {} };
};

/**
 * Detects code blocks and programming language
 */
const detectCode = (content) => {
  const codeIndicators = {
    fencedBlock: /```[\s\S]*```/,
    indentedBlock: /^(    |\t)/m,
    functionDef: /function\s+\w+\s*\(|def\s+\w+\s*\(|class\s+\w+/,
    syntax: /[{}\[\]();]|=>|&&|\|\||===?|!==?/,
    keywords: /\b(if|else|for|while|return|import|export|const|let|var)\b/
  };

  let score = 0;
  const metadata = {};

  if (codeIndicators.fencedBlock.test(content)) {
    score += 0.9;
    const langMatch = content.match(/```(\w+)/);
    if (langMatch) metadata.language = langMatch[1];
  }
  if (codeIndicators.functionDef.test(content)) score += 0.7;
  
  // Only add syntax score if there are multiple indicators (avoid false positives for plain text with parentheses)
  const syntaxMatches = content.match(/[{}\[\]();]|=>|&&|\|\||===?|!==?/g);
  if (syntaxMatches && syntaxMatches.length > 3) {
    score += 0.3;
  }
  
  if (codeIndicators.keywords.test(content)) score += 0.4;
  if (codeIndicators.indentedBlock.test(content)) score += 0.2;

  // Require a minimum confidence of 0.6 to classify as code (avoid plain text false positives)
  if (score < 0.6) {
    return {
      type: ContentTypes.MARKDOWN,
      confidence: 0.3,
      metadata: {}
    };
  }

  return {
    type: ContentTypes.CODE,
    confidence: Math.min(score, 1.0),
    metadata
  };
};

/**
 * Detects structured data patterns (KV pairs, metrics, etc.)
 */
const detectStructuredData = (content) => {
  const patterns = {
    keyValue: /^\s*[\w\s]+:\s*.+$/m,
    metrics: /\d+\.?\d*\s*(%|ms|MB|GB|items?|records?|days?|hours?)/i,
    labels: /^(Status|Result|Error|Warning|Info|Action|Risk):\s*/mi,
    sections: /^#{1,3}\s+.+$/m
  };

  let score = 0;
  const metadata = { patterns: [] };

  if (patterns.keyValue.test(content)) {
    const kvCount = (content.match(patterns.keyValue) || []).length;
    score += Math.min(kvCount * 0.15, 0.6);
    metadata.patterns.push('keyValue');
  }
  if (patterns.metrics.test(content)) {
    score += 0.3;
    metadata.patterns.push('metrics');
  }
  if (patterns.labels.test(content)) {
    score += 0.4;
    metadata.patterns.push('labels');
  }
  if (patterns.sections.test(content)) {
    score += 0.2;
    metadata.patterns.push('sections');
  }

  return {
    type: ContentTypes.STRUCTURED_DATA,
    confidence: Math.min(score, 1.0),
    metadata
  };
};

/**
 * Detects list structures
 */
const detectList = (content) => {
  const patterns = {
    unordered: /^\s*[-*•]\s+/m,
    ordered: /^\s*\d+[\.)]\s+/m,
    nested: /^\s{2,}[-*•\d]+[\.)]\s+/m,
    checkList: /^\s*\[[ x]\]\s+/mi
  };

  let score = 0;
  const metadata = { listTypes: [] };

  Object.entries(patterns).forEach(([type, pattern]) => {
    if (pattern.test(content)) {
      score += 0.5;
      metadata.listTypes.push(type);
    }
  });

  // Check list consistency
  const lines = content.split('\n');
  const listLines = lines.filter(l => 
    patterns.unordered.test(l) || patterns.ordered.test(l)
  );
  
  if (listLines.length > 2) {
    score += 0.3;
    metadata.itemCount = listLines.length;
  }

  return {
    type: ContentTypes.LIST,
    confidence: Math.min(score, 1.0),
    metadata
  };
};

/**
 * Detects chart/visualization data
 */
const detectChartData = (content) => {
  const indicators = {
    jsonMarkers: /JSON START[\s\S]*JSON END/,
    chartKeywords: /\b(chart|graph|plot|visualization|series|axis|legend)\b/i,
    dataArrays: /\[\s*\d+(\.\d+)?(\s*,\s*\d+(\.\d+)?)+\s*\]/,
    coordinates: /\(\s*\d+\s*,\s*\d+\s*\)/
  };

  let score = 0;
  const metadata = {};

  if (indicators.jsonMarkers.test(content)) {
    score = 0.95;
    metadata.format = 'json_markers';
  }
  if (indicators.chartKeywords.test(content)) score += 0.3;
  if (indicators.dataArrays.test(content)) score += 0.4;
  if (indicators.coordinates.test(content)) score += 0.2;

  return {
    type: ContentTypes.CHART_DATA,
    confidence: Math.min(score, 1.0),
    metadata
  };
};

/**
 * Detects alert/status message types
 */
const detectAlertType = (content) => {
  const patterns = {
    error: /^(error|failed|failure|exception):/mi,
    warning: /^(warning|caution|alert|attention):/mi,
    success: /^(success|completed|done|finished):/mi,
    info: /^(info|note|tip|hint):/mi
  };

  for (const [type, pattern] of Object.entries(patterns)) {
    if (pattern.test(content)) {
      return {
        type: type.toUpperCase(),
        confidence: 0.9,
        metadata: { alertType: type }
      };
    }
  }

  return { type: ContentTypes.MARKDOWN, confidence: 0, metadata: {} };
};

/**
 * Extracts structured sections from content
 */
export const extractSections = (content) => {
  const sections = [];
  const lines = content.split('\n');
  let currentSection = null;
  let insideCodeFence = false;

  const sectionPattern = /^(#{1,3}|[A-Z][A-Za-z\s]+:)\s*(.+)?$/;

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    // Track code fence boundaries — never split inside a fenced block
    if (trimmed.startsWith('```')) {
      insideCodeFence = !insideCodeFence;
      if (currentSection) currentSection.content.push(line);
      return;
    }

    if (insideCodeFence) {
      if (currentSection) currentSection.content.push(line);
      return;
    }

    const match = line.match(sectionPattern);
    if (match) {
      if (currentSection) {
        sections.push(currentSection);
      }
      currentSection = {
        title: match[2] || match[1].replace(/[:#]/g, '').trim(),
        content: [],
        startLine: index
      };
    } else if (currentSection) {
      currentSection.content.push(line);
    }
  });

  if (currentSection) {
    sections.push(currentSection);
  }

  return sections.map(s => ({
    ...s,
    content: s.content.join('\n').trim()
  }));
};

/**
 * Analyzes content complexity and suggests rendering strategy
 */
export const analyzeComplexity = (content) => {
  const metrics = {
    length: content.length,
    lines: content.split('\n').length,
    words: content.split(/\s+/).length,
    sections: extractSections(content).length,
    codeBlocks: (content.match(/```[\s\S]*?```/g) || []).length,
    tables: (content.match(/\|.*\|.*\|/g) || []).length,
    lists: (content.match(/^\s*[-*•\d]+[\.)]\s+/gm) || []).length
  };

  const complexity = {
    score: 0,
    factors: []
  };

  if (metrics.lines > 50) {
    complexity.score += 2;
    complexity.factors.push('long');
  }
  if (metrics.sections > 3) {
    complexity.score += 1;
    complexity.factors.push('multi-section');
  }
  if (metrics.codeBlocks > 0) {
    complexity.score += 1;
    complexity.factors.push('has-code');
  }
  if (metrics.tables > 0) {
    complexity.score += 1;
    complexity.factors.push('has-tables');
  }

  return {
    metrics,
    complexity,
    suggestion: complexity.score > 3 ? 'progressive' : 'immediate'
  };
};